import { useEffect, useRef, useState, useMemo } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import Layout from "../../layout/Layout";
import { InventoryHistoryAPI, fetchProductDropdownAPI } from "../../config";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Autocomplete from "@mui/material/Autocomplete";
import { MaterialReactTable } from "material-react-table";

import { apiData } from "../../services/api_service";
import { formatAED, formatNumber } from "../../utils/format";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import { saveAs } from "file-saver";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate } from "react-router-dom";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

function StockTransferReport() {
  const location = useLocation();
  const navigate = useNavigate();

  const { store_id, product_id, man_date, life_days } = location.state || {}; // default to empty object
  const [totalCount, setTotalCount] = useState(0);
  const [transactionData, setTransactionData] = useState([]);

  const [loading, setLoading] = useState(false);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);

  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [fromDate, setFromDate] = useState(null); // default to current date
  const [toDate, setToDate] = useState(null);
  const [searchFromDate, setSearchFromDate] = useState(null);
  const [searchToDate, setSearchToDate] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  let tempRefs = useRef(false);

  useEffect(() => {
    if (product_id && man_date && life_days && store_id) {
      setFilters([
        { id: "manufacture_date", value: man_date },
        { id: "life_days", value: life_days },
        { id: "store_id", value: store_id },
      ]);
      setSelectedProduct({ id: product_id, product_name: "" });
      setSearchTriggered((prev) => !prev); // trigger search
      tempRefs.current = true; // prevent initial fetchOrders call
    }
  }, [product_id, man_date, life_days, store_id]);

  useEffect(() => {
    if (tempRefs.current === false) {
      fetchOrders(1);
    }
  }, []);

  const fetchProducts = async (search = "") => {
    setLoadingProducts(true);
    try {
      const result = await apiData(
        "get",
        `${fetchProductDropdownAPI}?search=${search}`
      );

      if (result.success) {
        setProductOptions(result.data.result);
      }
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchOrders = async () => {
    const payload = {
      pageIndex: pageIndex,
      pageSize: pageSize,
      sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
      search: filters.length > 0 ? JSON.stringify(filters) : null,
      product_id: selectedProduct ? selectedProduct.id : null,
      from_date: searchFromDate || null,
      to_date: searchToDate || null,
    };
    try {
      setLoading(true);
      const response = await apiData("get", InventoryHistoryAPI, payload);
      if (response?.data) {
        // const filteredData = response.data.filter(
        //   (item) => item.transferred_from_id && item.transferred_store_id
        // );
        const filteredData = response.data;
        setTransactionData(filteredData);
      }
      setTotalCount(response.pagination);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [pageIndex, pageSize, filters, sorting, searchTriggered]);

  const handleExportData = async () => {
    try {
      setLoading(true);

      // Prepare payload with all filters, sorting, search, but unlimited pageSize
      const payload = {
        pageIndex: 0,
        pageSize: 1000000, // effectively "all" records
        sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
        search: filters.length > 0 ? JSON.stringify(filters) : null,
        product_id: selectedProduct ? selectedProduct.id : null,
        from_date: searchFromDate || null,
        to_date: searchToDate || null,
      };

      const response = await apiData("get", InventoryHistoryAPI, payload);

      if (!response?.data || response.data.length === 0) {
        alert("No data to export!");
        return;
      }

      const exportData = response.data.map((item, index) => ({
        "#": index + 1,
        "Product Name": item.product_name || "",
        "SKU Code": item.sku || "",
        "Store (Transferred From)": item.transferred_store_name || "",
        "Store (Transferred to)": item.store_name || "",
        "Transaction Type":
          item.transferred_from_id && item.transferred_store_id
            ? "Transfer"
            : item.change_type,
        // A real signed number (deducts are negative), shown with its +/- sign.
        "Quantity Transfer":
          item.transferred_from_id && item.transferred_store_id
            ? Number(item.quantity) || 0
            : item.change_type?.toLowerCase() === "deduct"
            ? -(Number(item.quantity) || 0)
            : Number(item.quantity) || 0,
        "In Stock":
          item.Balance_instock === null || item.Balance_instock === undefined || item.Balance_instock === ""
            ? ""
            : Number(item.Balance_instock),
        "Buying Price (AED)": item.buying_price != null && item.buying_price !== "" ? Number(item.buying_price) : "",
        "Selling Price (AED)": item.selling_price != null && item.selling_price !== "" ? Number(item.selling_price) : "",
        "Manufacture Date": item.manufacture_date
          ? moment(item.manufacture_date).format("DD-MM-YYYY")
          : "",
        "Expiration (No. of days)": item.life_days || 0,
        "Performed By": item.employee_name || "",
        "Performed Date": item.created_at
          ? moment(item.created_at).format("DD-MM-YYYY")
          : "",
        Notes: item.remarks || "",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      // Quantity (column G) keeps its +/- sign; the two prices (I, J) show two decimals.
      Object.keys(ws).forEach((addr) => {
        if (ws[addr] && ws[addr].t === "n") {
          if (/^G\d+$/.test(addr)) ws[addr].z = "+0.###;-0.###;0";
          else if (/^[IJ]\d+$/.test(addr)) ws[addr].z = "#,##0.00";
        }
      });
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "StockTransactions");

      const fileName = `StockTransactions_${moment().format(
        "DD_MM_YYYY"
      )}.xlsx`;
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "product_name",
        header: "Product Name",
        size: 50,
      },
      {
        accessorKey: "sku",
        header: "SKU Code",
        size: 50,
      },
      {
        accessorKey: "transferred_store_name",
        header: "Store (Transferred From)",
        size: 50,
      },
      {
        accessorKey: "store_name",
        header: "Store (Transferred to)",
        size: 50,
        Cell: ({ cell }) => {
          return cell.row.original.store_name
            ? cell.row.original.store_name
            : "N/A";
        },
      },
      {
        accessorKey: "change_type",
        header: "Transaction Type",
        size: 50,
        enableSorting: false,
        enableOptions: false,
        filterVariant: "select",
        filterSelectOptions: [
          { label: "Add", value: "add" },
          { label: "Deduct", value: "deduct" },
          { label: "Transfer", value: "transfer" },
        ],
        Cell: ({ row }) => {
          const { change_type, transferred_from_id, transferred_store_id } =
            row.original;

          let value = change_type;
          if (transferred_from_id && transferred_store_id) {
            value = "Transfer";
          }

          const style = {
            color:
              value?.toLowerCase() === "deduct"
                ? "red"
                : value?.toLowerCase() === "transfer"
                ? "blue"
                : "green",
            fontWeight: 600,
          };
          return <span style={style}>{value}</span>;
        },
      },
      {
        accessorKey: "quantity",
        header: "Quantity Transfer",
        size: 50,
        Cell: ({ row }) => {
          const {
            quantity,
            change_type,
            transferred_from_id,
            transferred_store_id,
          } = row.original;

          let displayValue;
          let style;

          if (transferred_from_id && transferred_store_id) {
            // ✅ For transfer, just show the transferred qty
            displayValue = formatNumber(quantity);
            style = { color: "blue", fontWeight: 600 };
          } else {
            displayValue =
              change_type?.toLowerCase() === "deduct"
                ? `-${formatNumber(quantity)}`
                : `+${formatNumber(quantity)}`;
            style = {
              color: change_type?.toLowerCase() === "deduct" ? "red" : "green",
              fontWeight: 600,
            };
          }

          return <span style={style}>{displayValue}</span>;
        },
      },
      {
        accessorKey: "Balance_instock",
        header: "In Stock",
        size: 50,
        Cell: ({ cell }) => (
          <span style={{ color: "green", fontWeight: "bold" }}>
            {cell.row.original.Balance_instock != null
              ? formatNumber(cell.row.original.Balance_instock)
              : "N/A"}
          </span>
        ),
      },
      {
        accessorKey: "buying_price",
        header: "Buying Price",
        size: 50,
        Cell: ({ cell }) => {
          return cell.row.original.buying_price
            ? formatAED(cell.row.original.buying_price)
            : "N/A";
        },
      },
      {
        accessorKey: "selling_price",
        header: "Selling Price",
        size: 50,
        Cell: ({ cell }) => {
          return cell.row.original.selling_price
            ? formatAED(cell.row.original.selling_price)
            : "N/A";
        },
      },
      {
        accessorKey: "manufacture_date",
        header: "Manufacture Date",
        size: 50,
        Cell: ({ cell }) => {
          return cell.row.original.manufacture_date
            ? moment(cell.row.original.manufacture_date).format("DD-MM-YYYY")
            : "N/A";
        },
        Filter: ({ column }) => {
          const value = column.getFilterValue() || null;

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                value={value ? dayjs(value, "YYYY-MM-DD") : null}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => {
                  if (newValue) {
                    const formatted = dayjs(newValue).format("YYYY-MM-DD");
                    column.setFilterValue(formatted);
                  } else {
                    column.setFilterValue(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="standard"
                    placeholder="Select Date"
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#fff !important",
                        opacity: 0.7,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {value && (
                            <IconButton
                              size="small"
                              onClick={() => column.setFilterValue(null)}
                              sx={{ color: "#fff" }}
                            >
                              ✕
                            </IconButton>
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        accessorKey: "life_days",
        header: "Expiration (No. of days)",
        size: 50,
        Cell: ({ cell }) => {
          return cell.row.original.life_days || 0;
        },
      },
      {
        accessorKey: "employee_name",
        header: "Performed By",
        size: 50,
      },
      {
        accessorKey: "created_at",
        header: "Performed Date",
        size: 50,
        Cell: ({ cell }) => {
          return moment(cell.row.original.created_at).format("DD-MM-YYYY");
        },
        Filter: ({ column }) => {
          const value = column.getFilterValue() || null;

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                value={value ? dayjs(value, "YYYY-MM-DD") : null}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => {
                  if (newValue) {
                    const formatted = dayjs(newValue).format("YYYY-MM-DD");
                    column.setFilterValue(formatted);
                  } else {
                    column.setFilterValue(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="standard"
                    placeholder="Select Date"
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#fff !important",
                        opacity: 0.7,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {value && (
                            <IconButton
                              size="small"
                              onClick={() => column.setFilterValue(null)}
                              sx={{ color: "#fff" }}
                            >
                              ✕
                            </IconButton>
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        accessorKey: "remarks",
        header: "Notes",
        size: 50,
        Cell: ({ cell }) => {
          return cell.row.original.remarks || "N/A";
        },
      },
    ],
    []
  );

  return (
    <Layout>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000} // hide after 3 seconds
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "#28a745",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Stock Transaction Report
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">
                Stock Transaction Report
              </Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} textAlign="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
              sx={{ ml: 2 }}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center" sx={{ my: 2 }}>
          <Grid item xs={12} md={4} lg={4}>
            <Autocomplete
              options={productOptions}
              getOptionLabel={(option) =>
                option.product_name
                  ? `${option.product_name} ${option.sku || ""}`
                  : ""
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={loadingProducts}
              value={selectedProduct}
              onInputChange={(e, value, reason) => {
                setSearchTerm(value);
                if (reason === "input") fetchProducts(value);
                else if (reason === "clear" || reason === "reset")
                  fetchProducts("");
              }}
              onChange={(e, newValue) => {
                setSelectedProduct(newValue);
              }}
              /* ⬇️ SHOW SELECTED PRODUCT INSIDE THE INPUT */
              renderInput={(params) => {
                const selected = selectedProduct;

                return (
                  <TextField
                    {...params}
                    label="Select a Product"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,

                      startAdornment: selected ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          {selected.product_image && (
                            <img
                              src={selected.product_image}
                              alt={selected.product_name}
                              style={{
                                width: 36,
                                height: 36,
                                objectFit: "contain",
                                borderRadius: 6,
                              }}
                            />
                          )}

                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span>
                              {selected.product_name} (SKU: {selected.sku})
                            </span>

                            <span
                              style={{ fontSize: "12px", color: "#1976d2" }}
                            >
                              Size: {selected.size || "NA"}, Colour:{" "}
                              {selected.color || "NA"}
                            </span>
                          </div>
                        </div>
                      ) : null,

                      // 🔥 Prevent duplicated text inside field
                      inputProps: {
                        ...params.inputProps,
                        value: selected ? "" : params.inputProps.value,
                      },
                    }}
                  />
                );
              }}
              /* ⬇️ HIGHLIGHT SELECTED PRODUCT IN DROPDOWN */
              renderOption={(props, option) => {
                const isSelected = selectedProduct?.id === option.id;
                const size = option.size || "NA";
                const color = option.color || "NA";

                return (
                  <li
                    {...props}
                    key={option.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 12px",
                      borderBottom: "1px solid #f0f0f0",
                      cursor: "pointer",
                      background: isSelected ? "#e6f3ff" : "white",
                      borderLeft: isSelected
                        ? "4px solid #1976d2"
                        : "4px solid transparent",
                    }}
                  >
                    {/* Product Image */}
                    {option.product_image && (
                      <img
                        src={option.product_image}
                        alt={option.product_name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "contain",
                          borderRadius: 6,
                          marginRight: 12,
                        }}
                      />
                    )}

                    {/* Product Info */}
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ color: "#333", marginBottom: 4 }}>
                        {option.product_name} <span>(SKU: {option.sku})</span>
                      </span>

                      <span style={{ fontSize: "0.85rem", color: "#007bff" }}>
                        {`Size: ${size}, Colour: ${color}`}
                      </span>
                    </div>
                  </li>
                );
              }}
              ListboxProps={{
                style: {
                  maxHeight: 250,
                  overflowY: "auto",
                  borderRadius: 8,
                  padding: 4,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={4} lg={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="From Date"
                value={fromDate}
                inputFormat="DD-MM-YYYY"
                onChange={(date) =>
                  setFromDate(date ? date.format("YYYY-MM-DD") : "")
                }
                renderInput={(params) => (
                  <Tooltip title="Select date to filter transactions by performed date">
                    <TextField {...params} fullWidth />
                  </Tooltip>
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={4} lg={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="To Date"
                value={toDate}
                inputFormat="DD-MM-YYYY"
                onChange={(date) =>
                  setToDate(date ? date.format("YYYY-MM-DD") : "")
                }
                renderInput={(params) => (
                  <Tooltip title="Select date to filter transactions by performed date">
                    <TextField {...params} fullWidth />
                  </Tooltip>
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
            spacing={2}
            sx={{ mt: 1 }}
          >
            <Grid item>
              <Button
                variant="contained"
                onClick={() => {
                  setPageIndex(0); // reset page
                  setSearchFromDate(fromDate); // only now send dates
                  setSearchToDate(toDate);
                  setSearchTriggered((prev) => !prev); // ✅ trigger fetchOrders
                  // fetchOrders();
                }}
              >
                Search
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setSelectedProduct(null); // clear product
                  setFromDate(null);
                  setToDate(null);
                  setSearchFromDate(null);
                  setSearchToDate(null);
                  setPageIndex(0); // reset pagination
                  setSearchTriggered((prev) => !prev); // refetch with no product filter
                }}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <MaterialReactTable
          columns={columns}
          data={transactionData}
          enableGlobalFilter={false}
          enableRowNumbers
          enableFullScreenToggle={false}
          // className="mat-react-tbl"
          state={{
            isLoading: loading,
            pagination: { pageIndex, pageSize },
            sorting,
            columnFilters: filters,
          }}
          manualPagination
          manualSorting
          manualFiltering
          enableStickyHeader
          rowCount={totalCount}
          onPaginationChange={(updater) => {
            const paginationState = updater({
              pageIndex,
              pageSize,
            });
            const { pageIndex: newPageIndex, pageSize: newPageSize } =
              paginationState;
            setPageIndex(newPageIndex);
            setPageSize(newPageSize);
          }}
          muiTableHeadCellProps={{
            sx: {
              padding: "8px",
              border: "1px solid #ccc",
              color: "#fff !important",
              fontWeight: "bold",
              backgroundColor: "#1976d2",
              "& svg": {
                color: "#fff !important",
              },
              "& .MuiTableSortLabel-root": {
                color: "#fff !important",
                "&.Mui-active": {
                  color: "#fff !important",
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: "#fff !important",
              },
              "& .MuiInputBase-input": {
                color: "#fff !important",
                textFillColor: "#fff !important",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#fff !important",
                opacity: 0.8,
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#fff !important",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff !important",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff !important",
              },
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: "calc(100vh - 240px)",
              minHeight: 450,
              overflowY: "auto",
            },
          }}
          onSortingChange={setSorting}
          onColumnFiltersChange={setFilters}
          renderTopToolbarCustomActions={({ table }) => (
            <>
              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  p: "0.5rem",
                  justifyContent: "space-between",
                  flexGrow: 1,

                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* RIGHT SIDE BUTTON */}
                  <Tooltip title="Export data to excel sheet" arrow>
                    <Button
                      onClick={handleExportData}
                      style={{ background: "#1a73e829" }}
                      startIcon={<FileDownload />}
                      // startIcon={<IconTableExport />}
                    >
                      Export To Excel
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </>
          )}
        />
      </Grid>
    </Layout>
  );
}

export default StockTransferReport;
